import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { PlatformService } from '@core/services';
import { BillingV2Service } from '@core/services/lingo2-account/billing-v2.service';
import { BillingApiAccountResponseDto } from '@lingo2-billing-sdk/models';
import { ChangableComponent } from '@models/changable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { format } from 'date-fns';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-payment-card',
  templateUrl: './payment-card.component.html',
  styleUrls: ['./payment-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentCardComponent extends ChangableComponent {
  @Input() public set account(value: BillingApiAccountResponseDto) {
    if (value) {
      this._account = value;
    }
  }

  public get account() {
    return this._account;
  }

  @Input() public set view(value: 'simple' | 'full') {
    if (value) {
      this._view = value;
    } else {
      this._view = 'full';
    }
  }

  public get view() {
    return this._view;
  }

  @Input() selected: boolean;
  @Input() is_test: boolean;

  @Output() openHistory = new EventEmitter<string>();
  @Output() getMoney = new EventEmitter<string>();
  @Output() onChange = new EventEmitter<string>();
  @Output() onSelect = new EventEmitter<boolean>();
  @Output() onEdit = new EventEmitter<BillingApiAccountResponseDto>();

  public _account: BillingApiAccountResponseDto;
  public _view: 'simple' | 'full';

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    protected readonly billingV2Service: BillingV2Service,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public onOpenHistory(): void {
    this.openHistory.emit(this.account.providerId);
  }

  public onGetMoney(): void {
    this.getMoney.emit(this.account.id);
  }

  public deleteAccount() {
    this.billingV2Service
      .deleteAccount(this.account.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => this.onChange.emit(String(new Date().getTime())),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public setDefault() {
    this.billingV2Service
      .setAccountDefault(this.account.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => this.onChange.emit(String(new Date().getTime())),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
        },
      });
  }

  public get accountLabel() {
    if (this.account.cardAccount) {
      return `**** **** **** ${this.account.cardAccount.label}`;
    } else if (this.account.bankAccount) {
      return `*********${this.account.bankAccount.label}`;
    }
  }

  public get accountExpirationMY() {
    if (this.account.cardAccount) {
      const date = this.setUTCDate(new Date(this.account.cardAccount.expiresAt.toString()));
      return format(date, 'MM/yy');
    } else {
      return '---';
    }
  }

  public setUTCDate(date: Date) {
    return date.setTime(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
  }

  public get accountExpire() {
    if (this.account.cardAccount) {
      const currentYear = format(this.setUTCDate(new Date()), 'yy');
      const currentMonth = format(this.setUTCDate(new Date()), 'MM');
      const cardYear = format(this.setUTCDate(new Date(this.account.cardAccount.expiresAt.toString())), 'yy');
      const cardMonth = format(this.setUTCDate(new Date(this.account.cardAccount.expiresAt.toString())), 'MM');

      if (currentYear > cardYear) {
        return true;
      } else if (currentYear < cardYear) {
        return false;
      } else {
        return currentMonth > cardMonth;
      }
    } else {
      return false;
    }
  }

  public get accountType() {
    if (this.account.cardAccount) {
      return this.account.cardAccount.brand.toLowerCase();
    } else {
      return '---';
    }
  }

  public get isDefault() {
    return this.account.isDefault;
  }

  public selectAcc() {
    this.onSelect.emit(true);
  }

  public get isTestCard() {
    return this.is_test;
  }
}
